import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  CircularProgress,
  TablePagination,
} from "@mui/material";

import { useEstimateApi } from "../../hooks/useEstimateApi.ts";
import { EstimateDto } from "../../contracts/estimates/types.ts";
import EstimateSearchBar from "../../components/common/EstimateSearchBar.tsx";
import { fmtDateTime } from "../../utils/dateFormat.ts";
import {
  EstimateSearchCriteria,
  EstimateSearchResponse,
  defaultCriteria,
  defaultPagination,
} from "../../contracts/estimates/searchTypes.ts";

export default function PopupLoadEstimate() {
  const { searchEstimates } = useEstimateApi();

  const [criteria, setCriteria] = useState<EstimateSearchCriteria>(defaultCriteria);
  const [page, setPage] = useState(0); // 0-based for TablePagination
  const [pageSize] = useState(defaultPagination.pageSize);

  const [response, setResponse] = useState<EstimateSearchResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const json = await searchEstimates(criteria, {
        page: page + 1,
        pageSize,
        sortField: "createdTimestamp",
        sortOrder: "desc",
      });
      setResponse(json);
    } catch (e: any) {
      setError(e?.message ?? String(e));
    } finally {
      setLoading(false);
    }
  }, [criteria, page, pageSize, searchEstimates]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSearch = (newCriteria: EstimateSearchCriteria) => {
    setCriteria(newCriteria);
    setPage(0);
  };

  const handleReset = () => {
    setCriteria(defaultCriteria);
    setPage(0);
  };

  const sendToParentAndClose = (estimate: EstimateDto) => {
    if (!window.opener) {
      alert(
        "부모 창(opener)이 없어 데이터를 전달할 수 없습니다. EstimateWizard에서 연 팝업인지 확인하세요."
      );
      return;
    }
    window.opener.postMessage(
      { type: "ESTIMATE_SELECTED", payload: estimate },
      window.location.origin
    );
    window.close();
  };

  const handleChangePage = (_: unknown, newPage: number) => setPage(newPage);

  const rows = response?.data ?? [];
  const total = response?.total ?? 0;

  return (
    <Box sx={{ p: 2, width: 1000 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>
        이전 견적서 내용 불러오기
      </Typography>

      <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
        검색 조건으로 필터링한 결과를 페이지 단위로 표시합니다. 선택하면 현재 작성 중인 견적서 내용이 대체됩니다.
      </Typography>

      <EstimateSearchBar onSearch={handleSearch} onReset={handleReset} />

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mb: 1 }}>
        <Button variant="outlined" onClick={() => window.close()}>
          닫기
        </Button>
      </Box>

      {loading && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 2 }}>
          <CircularProgress size={18} />
          <Typography variant="body2">불러오는 중…</Typography>
        </Box>
      )}

      {error && (
        <Typography variant="body2" color="error" sx={{ py: 2 }}>
          {error}
        </Typography>
      )}

      {!loading && !error && (
        <>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 200 }}>공급업체</TableCell>
                <TableCell sx={{ width: 200 }}>견적서 제목</TableCell>
                <TableCell sx={{ width: 200 }}>업체명</TableCell>
                <TableCell sx={{ width: 200 }}>작성일자</TableCell>
                <TableCell sx={{ width: 110 }} />
              </TableRow>
            </TableHead>

            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id}>
                  <TableCell>{r.companyInCharge}</TableCell>
                  <TableCell>{r.content.contentTitle}</TableCell>
                  <TableCell>{r.cover.companyName}</TableCell>
                  <TableCell>{fmtDateTime(r.createdTimestamp)}</TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => sendToParentAndClose(r)}
                    >
                      선택
                    </Button>
                  </TableCell>
                </TableRow>
              ))}

              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                      검색 결과가 없습니다.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={pageSize}
            rowsPerPageOptions={[pageSize]}
            onRowsPerPageChange={() => {}}
          />
        </>
      )}
    </Box>
  );
}
